/* Work broadcasts are invalidations, not trusted state. Fetch authorized HTML. */
(function () {
    var socket = null;
    var reconnectTimer = null;
    var timer = null;
    var attempts = 0;
    var running = false;
    var pending = new Set();
    // Task lists patch rows in place instead of refetching, so a status change
    // never re-sorts the table under the user. Only a new task or an event
    // naming nothing (reconnect, tab refocus) refetches the whole task list.
    // Rows stay where they are until then, even once they no longer match the
    // filter or sort.
    var rowTasks = new Set();
    var rowPlans = new Set();
    var taskListStale = false;

    function onWorkPage() {
        return location.pathname.indexOf('/work/') === 0 || !!document.querySelector('[data-work-live]');
    }

    function editing() {
        var focus = document.activeElement;
        var dirtyText = Array.from(document.querySelectorAll(
            '[id^="item-description-"] textarea, [id^="plan-description-"] textarea, [data-work-title], [data-work-next-action]'
        )).some(function (input) { return input.value !== input.defaultValue; });
        var dirtyQuickAdd = Array.from(document.querySelectorAll('[data-work-quick-add]')).some(function (input) {
            var panel = input.closest('.collapse');
            return input.value !== input.defaultValue && panel && panel.classList.contains('collapse--open');
        });
        var dirtyDate = Array.from(document.querySelectorAll('[data-work-date-input]')).some(function (input) {
            return input.value !== input.defaultValue;
        });
        return dirtyText || dirtyQuickAdd || dirtyDate || (focus && focus.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]')) ||
            document.querySelector('.dropdown.show, .htmx-request, .sortable-ghost, input[name="tmp_file_ids"], ' +
                '[id^="item-files-"] .collapse--open form, [id^="plan-files-"] .collapse--open form');
    }

    function schedule(planId, taskId, parentTaskId, created) {
        pending.add(String(planId || '*'));
        if (taskId) {
            pending.add('task:' + String(taskId));
            rowTasks.add(String(taskId));
            if (parentTaskId) {
                pending.add('task:' + String(parentTaskId));
                rowTasks.add(String(parentTaskId));
            }
        } else if (planId && planId !== '*') {
            rowPlans.add(String(planId));
        }
        if (created || (!taskId && (!planId || planId === '*'))) taskListStale = true;
        if (!timer) timer = setTimeout(flush, 180);
    }

    function replaceRegion(current, incoming) {
        // Never overwrite a draft which failed its blur-save validation.
        var dirty = Array.from(current.querySelectorAll('textarea, [data-work-date-input]')).some(function (input) {
            return input.value !== input.defaultValue;
        }) || Array.from(current.querySelectorAll('[data-work-quick-add]')).some(function (input) {
            var panel = input.closest('.collapse');
            return input.value !== input.defaultValue && panel && panel.classList.contains('collapse--open');
        });
        if (dirty) return false;
        var collapseStates = Array.from(current.querySelectorAll('.collapse[id]')).map(function (el) {
            return {id: el.id, open: el.classList.contains('collapse--open')};
        });
        current.replaceWith(incoming);
        // Scripts parsed through a <template> never run, and the Files card's
        // uploader wires itself with an inline script. Recreate each one so it runs.
        // The other sockets (acqs-ws.js) swap through htmx.ajax, which runs scripts
        // itself; moving this snapshot onto htmx would make this unnecessary.
        incoming.querySelectorAll('script').forEach(function (inert) {
            var script = document.createElement('script');
            Array.from(inert.attributes).forEach(function (attr) { script.setAttribute(attr.name, attr.value); });
            script.textContent = inert.textContent;
            inert.replaceWith(script);
        });
        collapseStates.forEach(function (state) {
            var el = incoming.querySelector('#' + CSS.escape(state.id));
            if (el) el.classList.toggle('collapse--open', state.open);
        });
        if (window.htmx) {
            htmx.process(incoming);
            htmx.trigger(incoming, 'htmx:load', {elt: incoming});
            htmx.trigger(incoming, 'htmx:afterSettle', {elt: incoming});
        }
        return true;
    }

    async function refreshSurface(marker) {
        var response = await fetch(marker.dataset.workUrl, {
            headers: {'X-Requested-With': 'XMLHttpRequest'}, credentials: 'same-origin'
        });
        if (!marker.isConnected) return;
        if (response.status === 404) {
            if (marker.closest('.sheet')) {
                if (window.rfSheet) rfSheet.close();
            } else {
                location.assign('/work/plans/');
            }
            return;
        }
        // Shared permission denial can return a 200 HTML page. Never treat it as a snapshot.
        if (!response.ok || !(response.headers.get('Content-Type') || '').includes('application/json')) return;
        var data = await response.json();
        if (!marker.isConnected) return;
        if (editing()) { schedule(marker.dataset.workPlan, marker.dataset.workTask); return; }
        var fragment = document.createElement('template');
        fragment.innerHTML = data.html;
        var deferred = false;
        Array.from(fragment.content.children).forEach(function (incoming) {
            if (!incoming.id) return;
            var current = document.getElementById(incoming.id);
            if (current && !replaceRegion(current, incoming)) deferred = true;
        });
        var title = document.getElementById(marker.dataset.workName);
        // Editable task titles arrive in their own snapshot region. Rewriting
        // a textarea's textContent here would reset its default value.
        if (title && !title.matches('[data-work-title]')) title.textContent = data.name;
        if (deferred) schedule(marker.dataset.workPlan, marker.dataset.workTask);
    }

    async function refreshRow(row) {
        var response = await fetch(row.dataset.workRowUrl, {credentials: 'same-origin'});
        if (!row.isConnected) return;
        if (response.status === 204 || response.status === 404) { row.remove(); return; }
        if (!response.ok) return;
        var html = await response.text();
        if (!row.isConnected) return;
        if (editing()) { schedule(row.dataset.workPlan, row.dataset.workTaskRow); return; }
        var fragment = document.createElement('template');
        fragment.innerHTML = html.trim();
        var incoming = fragment.content.firstElementChild;
        // A shared permission denial can return a 200 page; only swap a row.
        if (!incoming || incoming.id !== row.id) return;
        if (!replaceRegion(row, incoming)) schedule(row.dataset.workPlan, row.dataset.workTaskRow);
    }

    function taskListJobs(tasks, plans, listStale) {
        var rows = Array.from(document.querySelectorAll('[data-work-task-row][data-work-row-url]'));
        var onTaskList = location.pathname === '/work/tasks/' && document.querySelector('[data-work-task-list-region]');
        if (onTaskList && listStale) return [refreshList()];
        var named = rows.filter(function (row) {
            return tasks.has(row.dataset.workTaskRow) || plans.has(row.dataset.workPlan);
        });
        // A subtask that is archived or added moves the count on the chip above
        // it, so refresh the parent row too when only the child was named.
        var parents = new Set(named.map(function (row) { return row.dataset.workTaskParent; }).filter(Boolean));
        return named.concat(rows.filter(function (row) {
            return parents.has(row.dataset.workTaskRow) && named.indexOf(row) === -1;
        })).map(refreshRow);
    }

    async function refreshList() {
        var current = document.querySelector('[data-work-plan-list]');
        var taskCurrent = document.querySelector('[data-work-task-list-region]');
        var list = current || taskCurrent;
        if (!list || (location.pathname !== '/work/plans/' && location.pathname !== '/work/tasks/')) return;
        var url = new URL(location.href);
        url.searchParams.delete('sheet');
        var response = await fetch(url.pathname + url.search, {credentials: 'same-origin'});
        if (!response.ok || !list.isConnected) return;
        var html = await response.text();
        if (!list.isConnected) return;
        if (editing()) { schedule('*'); return; }
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = current ? doc.querySelector('[data-work-plan-list]') : doc.querySelector('[data-work-task-list-region]');
        if (!incoming) return;
        replaceRegion(list, incoming);
        var tabs = document.getElementById('tabs-bar');
        var newTabs = doc.getElementById('tabs-bar');
        if (tabs && newTabs) replaceRegion(tabs, newTabs);
        // Keep the filter bar itself: it may contain unsubmitted search text.
    }

    async function flush() {
        timer = null;
        if (!onWorkPage()) { pending.clear(); return; }
        if (document.hidden) return;
        if (running || editing()) {
            timer = setTimeout(flush, 500);
            return;
        }
        running = true;
        var batch = new Set(pending);
        var tasks = new Set(rowTasks);
        var plans = new Set(rowPlans);
        var listStale = taskListStale;
        pending.clear();
        rowTasks.clear();
        rowPlans.clear();
        taskListStale = false;
        try {
            var markers = Array.from(document.querySelectorAll('[data-work-live]')).filter(function (marker) {
                return batch.has('*') || batch.has(marker.dataset.workPlan) || batch.has('task:' + marker.dataset.workTask);
            });
            var listJobs = document.querySelector('[data-work-plan-list]')
                ? [refreshList()] : taskListJobs(tasks, plans, listStale);
            await Promise.all(markers.map(refreshSurface).concat(listJobs));
        } catch (error) {
            // A network interruption leaves DB state intact; reconnect/catch-up retries the read.
            if (!(error instanceof TypeError)) throw error;
            pending.add('*');
            taskListStale = true;
        } finally {
            running = false;
            if (pending.size && !timer) timer = setTimeout(flush, 1000);
        }
    }

    function connect() {
        if (!onWorkPage() || socket) return;
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
        var ws = new WebSocket((location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws/work/');
        socket = ws;
        ws.onopen = function () { attempts = 0; schedule('*'); };
        ws.onmessage = function (event) {
            var data = JSON.parse(event.data);
            if (data.type === 'work_changed') {
                schedule(data.plan_id, data.task_id, data.parent_task_id, data.created);
            }
        };
        ws.onclose = function (event) {
            if (socket !== ws) return;
            socket = null;
            if (!onWorkPage() || event.code === 4401 || event.code === 4403 || attempts >= 10) return;
            reconnectTimer = setTimeout(connect, Math.min(2000 * Math.pow(1.5, attempts++), 30000));
        };
    }

    function checkConnection() {
        if (onWorkPage()) {
            if (!reconnectTimer && attempts < 10) connect();
        }
        else {
            if (reconnectTimer) clearTimeout(reconnectTimer);
            reconnectTimer = null;
            if (socket) { var previous = socket; socket = null; previous.close(); }
            pending.clear();
        }
    }
    document.body.addEventListener('htmx:pushedIntoHistory', checkConnection);
    document.body.addEventListener('htmx:afterSwap', checkConnection);
    document.body.addEventListener('htmx:load', checkConnection);
    // A sheet's work card arrives through rfSheet, not an htmx swap, so connect
    // when it opens. Otherwise the socket opens on the sheet's first swap, and
    // its catch-up refresh replaces whatever that swap just showed.
    document.body.addEventListener('sheet:ready', checkConnection);
    window.addEventListener('popstate', checkConnection);
    document.addEventListener('visibilitychange', function () {
        if (!document.hidden && onWorkPage()) { checkConnection(); schedule('*'); }
    });
    window.addEventListener('online', function () { attempts = 0; checkConnection(); schedule('*'); });
    checkConnection();
})();
